import type {AmbassadorshipRequestBrandSerializer, TikTokOpportunitySerializer} from "@/generated/DjangoTypes";
import {i18n} from "@/i18n";
import type {
  TAmbassadorshipRatingSubview,
  TCountry,
  TInviteCategory,
  TPermission,
  TSettingsAccountSection,
  TSettingsAdminSection,
  TSettingsScopeSection,
  TSupportedCampaignCountry,
  TTemplateForm,
} from "@/store/types";

export const AGENCY_ROUTES_WITHOUT_NAVIGATION = ["/campaigns/", "/influencers-list/"];

export const AMBASSADORSHIP_REQUEST_STATUS_LABEL_KEYS = {
  accepted: "referral:ambassadorship_request_status_accepted",
  cancelled: "referral:ambassadorship_request_status_cancelled",
  declined: "referral:ambassadorship_request_status_declined",
  expired: "referral:ambassadorship_request_status_expired",
  pending: "referral:ambassadorship_request_status_pending",
} as const satisfies Record<NonNullable<AmbassadorshipRequestBrandSerializer["status"]>, string>;

export const AMBASSADORSHIP_SUBVIEW_TITLE_KEYS = {
  accept: "referral:accept_ambassador",
  decline: "referral:decline_ambassador",
  request: "influencer_invite:invite_as_ambassador",
} as const satisfies Record<TAmbassadorshipRatingSubview["type"], string>;

export const BRAND_BIO_TOOLTIP_ID = "brand-bio-tooltip";

export const CARD_BRANDS: Record<string, string> = {
  amex: require("@/assets/images/payment_method_amex.webp"),
  discover: require("@/assets/images/payment_method_discover.webp"),
  link: require("@/assets/images/payment_method_link.webp"),
  mastercard: require("@/assets/images/payment_method_mastercard.webp"),
  paypal: require("@/assets/images/payment_method_paypal.webp"),
  visa: require("@/assets/images/payment_method_visa.webp"),
} as const;

export const COMMUNITY_CODES_CSV_ANCHOR_ID = "community-codes-csv-anchor";

export const COUNTRIES = [
  "Switzerland",
  "Germany",
  "Austria",
  "France",
  "Italy",
  "Liechtenstein",
  "Luxembourg",
  "United States",
  "United Kingdom",
] as const;

export const COUNTRY_CODE_TO_CAMPAIGN_LOCATION: Record<TSupportedCampaignCountry, TCountry> = {
  AT: "Austria",
  CH: "Switzerland",
  DE: "Germany",
  GB: "United Kingdom",
  US: "United States",
};

export const COUNTRY_FILTERS: TCountry[] = ["Switzerland", "Germany", "Austria", "United States", "United Kingdom"];

export const FULL_DATE_FORMAT = "DD.MM.YYYY";

export const INFINITY_THRESHOLD = 1000;

export const OTHER_TEMPLATE_ID = 9999;

export const INITIAL_TEMPLATE_STATE: TTemplateForm = {
  id: OTHER_TEMPLATE_ID,
  name: i18n.t("Other"),
  translations: [
    {
      language_code: "de_CH",
      title: "",
      body: "",
    },
    {
      language_code: "en_EN",
      title: "",
      body: "",
    },
  ],
};

export const INVITE_CATEGORIES: TInviteCategory[] = [
  "ADMIN",
  "FAVORITE",
  "FOLLOWS_YOU",
  "GROUPS",
  "LIKED_YOU",
  "RECOMMENDED",
  "WORKED_WITH",
] as const;

export const LANGUAGE_OPTIONS = [
  {code: "de_CH", i18nextCode: "ger", name: "german"},
  {code: "en_EN", i18nextCode: "en", name: "english"},
] as const;

export const LEARN_ABOUT_US_OPTIONS = [
  "GoogleAds",
  "LinkedinAds",
  "ContactedByUs",
  "DragonsDen",
  "Media",
  "ETH",
  "DMEXCO",
  "OMR",
  "RecommendationFromAnotherCompany",
  "RecommendationFromInfluencer",
  "Agency",
  "Other",
] as const;

export const MAX_RATING_VALUE = 5;

export const PERMISSIONS = [
  "REFLUENCED_COMPANY_ADMIN",
  "REFLUENCED_INFLUENCER_ADMIN",
  "ADMIN",
  "WRITE",
  "PURCHASE",
  "READ",
] as const;

export const PERMISSION_LABEL_KEYS: Record<
  TPermission,
  `multi_account:${"admin" | "purchase" | "read" | "write"}_permissions`
> = {
  ADMIN: "multi_account:admin_permissions",
  PURCHASE: "multi_account:purchase_permissions",
  READ: "multi_account:read_permissions",
  REFLUENCED_COMPANY_ADMIN: "multi_account:admin_permissions",
  REFLUENCED_INFLUENCER_ADMIN: "multi_account:admin_permissions",
  WRITE: "multi_account:write_permissions",
};

export const CONTENT_APPROVAL_MAX_REVISIONS = 2;

export const RATING_COMMENT_MAX_CHARS = 500;

export const ROUTES_WITHOUT_NAVBAR = ["/embed/", "/join/", "/onboarding"];

export const ROUTES_WITHOUT_FOOTER = [
  "/embed/",
  "/login",
  "/onboarding",
  "/register",
  "/request-reset",
  "/reset-password",
  "/search-visibility",
  "/verify-email",
  "/verify-email-change",
];

export const SETTINGS_ACCOUNT_SECTIONS: TSettingsAccountSection[] = [
  "account_profile",
  "account_preferences",
  "account_password",
];

export const SETTINGS_ADMIN_SECTIONS: TSettingsAdminSection[] = ["admin_settings", "admin_billing"];

// Sections a settings deep link (`/.../edit?section=`) may open; mirrors TSettingsScopeSection.
export const SETTINGS_SCOPE_SECTIONS = [
  "admin_billing",
  "admin_settings",
  "ambassadors",
  "billing",
  "info",
  "users",
] as const satisfies readonly TSettingsScopeSection[];

export const SUPPORTED_CAMPAIGN_COUNTRIES = ["DE", "AT", "CH", "GB", "US"] as const;

export const TIKTOK_SEARCH_OPPORTUNITY_STYLES: Record<
  TikTokOpportunitySerializer["type"],
  {badge: string; bar: string}
> = {
  gap: {
    badge: "bg-danger-light-red text-neutral-black",
    bar: "bg-danger-medium-red",
  },
  quick_win: {
    badge: "bg-success-light-green text-success-dark-green",
    bar: "bg-success-medium-green",
  },
  threat: {
    badge: "bg-warning-light-yellow text-neutral-black",
    bar: "bg-warning-dark-yellow",
  },
  unclaimed: {
    badge: "bg-primary-light-solid text-primary-dark-solid",
    bar: "bg-primary-dark-solid",
  },
};

export const URL_REGEX = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

export const VAT_PERCENTAGE = 8.1;

export const VIDEO_EXTENSIONS = ["mov", "mp4"];
